/* eslint-disable jsx-a11y/anchor-is-valid */
import {useEffect, useState} from 'react'
import axios from 'axios'
import {useIntl} from 'react-intl'
import {StepProps} from '../IAppModels'
import {IPatientData} from '../../../interface/IPatientData'
import {KTIcon} from '../../../../../_metronic/helpers'
import CustomCalendar from '../../../../../_metronic/layout/calendar'
import {countries, Country} from '../../../../../app/modules/wizards/components/steps/countries'
import countriesData from '../../../../../app/modules/wizards/components/steps/countries.json'

const Step2 = ({
  data,
  updateData,
  hasError,
  handleStep2ValidationSuccess,
  handleStep2ValidationError,
  errorMessageProduct,
  errorMessageBenefit,
}: StepProps) => {
  const {formatMessage: f} = useIntl()
  const [benefits, setBenefits] = useState<IPatientData['benefits']>([])
  const [selectedBenefit, setSelectedBenefit] = useState<IPatientData['benefits'][0] | null>(null)

  // Phone states
  const [selectedCountry, setSelectedCountry] = useState(countries[0]) 
  const [localPhoneNumber, setLocalPhoneNumber] = useState('')
  const [phoneError, setPhoneError] = useState<string | null>(null)
  const [allCountries, setAllCountries] = useState<Country[]>(countries)
  const [searchTerm, setSearchTerm] = useState('')

  const filteredCountries = allCountries.filter(
    (c) =>
      c.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.dialCode.includes(searchTerm) ||
      c.code.toLowerCase().includes(searchTerm.toLowerCase())
  )

  // Validation states
  const [isPlanValid, setIsPlanValid] = useState(false)
  const [isBenefitsValid, setIsBenefitsValid] = useState(false)
  const [isTreatmentTypeValid, setIsTreatmentTypeValid] = useState(true)
  const [isIndateValid, setIsIndateValid] = useState(true)

  useEffect(() => {
    let shouldUpdate = false
    const newAppBasic = {...data.appBasic}

    if (newAppBasic.memberData && newAppBasic.memberData.benefits) {
      setBenefits(newAppBasic.memberData.benefits)
    }

    // Initialize treatment type to 'Rawat inap' by default if not already set
    if (!newAppBasic.treatmentType) {
      newAppBasic.treatmentType = 'Rawat inap'
      setIsTreatmentTypeValid(true)
      shouldUpdate = true
    }

    // Set current date for indate if treatment type is 'Rawat jalan'
    if (newAppBasic.treatmentType === 'Rawat jalan') {
      const currentDate = new Date().toISOString().split('T')[0] // Format: YYYY-MM-DD
      if (newAppBasic.indate !== currentDate) {
        newAppBasic.indate = currentDate
        shouldUpdate = true
      }
    }

    if (shouldUpdate) {
      updateData({appBasic: newAppBasic})
    }
  }, [data.appBasic.memberData, data.appBasic.treatmentType, data.appBasic.indate, updateData])

  useEffect(() => {
    const phone = data.appBasic.phoneNumber || ''
    if (phone.startsWith('+')) {
      const sorted = [...allCountries].sort((a, b) => b.dialCode.length - a.dialCode.length)
      const country = sorted.find((c) => phone.startsWith(c.dialCode))
      if (country) {
        setSelectedCountry(country)
        const local = phone.substring(country.dialCode.length)
        setLocalPhoneNumber(local)
      } else {
        setSelectedCountry(countries[0])
        const local = phone.replace(/^\+\d+/, '')
        setLocalPhoneNumber(local)
      }
    } else if (phone.startsWith('0')) {
      setSelectedCountry(countries[0])
      setLocalPhoneNumber(phone.substring(1))
    } else {
      setSelectedCountry(countries[0])
      setLocalPhoneNumber(phone)
    }
  }, [data.appBasic.phoneNumber, allCountries])

  useEffect(() => {
    const formatted = (countriesData as any[])
      .map((c) => ({
        name: c.name,
        code: c.code,
        dialCode: c.dial_code,
        emoji: c.flag,
        flagUrl: `https://flagcdn.com/${String(c.code).toLowerCase()}.svg`,
      }))
      .filter((c) => c.dialCode && c.dialCode.length > 1)

    formatted.sort((a, b) => a.name.localeCompare(b.name))
    setAllCountries(formatted)
    setSelectedCountry((prev) => {
      const match = formatted.find((c) => c.code === prev.code)
      return match || prev
    })
  }, [])

  const handleBenefitChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const benefitId = e.target.value

    // Check if the selected value is not the default placeholder
    if (benefitId) {
      const selected = benefits.find((benefit) => benefit.id === parseInt(benefitId))
      setSelectedBenefit(selected || null)

      // Check if the selected benefit allows both treatment types or only outpatient
      const allowsBothTreatmentTypes =
        selected &&
        [
          'INPATIENT',
          'INPATIENT-OUTPATIENT-KHUSUS',
          'MATERNITY',
          'LIMIT BOOSTER TAHUNAN',
          'LIFETIME LIMIT BENEFIT',
        ].includes(selected.benefitName)

      // If benefit doesn't allow both treatment types, force set to Rawat jalan
      const treatmentType = allowsBothTreatmentTypes
        ? data.appBasic.treatmentType || 'Rawat inap'
        : 'Rawat jalan'

      console.log('treatmentType', treatmentType)

      updateData({
        appBasic: {
          ...data.appBasic,
          benefitDetail: selected || null,
          benefit: selected ? selected.benefitName : '',
          plan: selected ? selected.planName : '',
          treatmentType: treatmentType,
        },
      })

      // Validate fields after selection
      validateFields({
        ...data.appBasic,
        benefitDetail: selected || null,
        benefit: selected ? selected.benefitName : '',
        plan: selected ? selected.planName : '',
        treatmentType: treatmentType,
      })
    } else {
      // Reset selectedBenefit if the default option is selected
      setSelectedBenefit(null)
      updateData({
        appBasic: {
          ...data.appBasic,
          benefitDetail: null,
          benefit: '',
          plan: '',
        },
      })
    }
  }

  const handleDropdownOpen = () => {
    // Dropdown open logic
  }

  const validatePhone = (localNumber: string, country: Country) => {
    if (localNumber.trim() === '') {
      return f({id: 'CLAIM.REGISTER.PHONE_REQUIRED'})
    }

    const digitsOnly = /^\d+$/.test(localNumber)
    if (!digitsOnly) {
      return f({id: 'CLAIM.REGISTER.PHONE_DIGITS_ONLY'})
    }

    if (localNumber.startsWith('0')) {
      return f({id: 'CLAIM.REGISTER.PHONE_NO_LEADING_ZERO'})
    }

    if (localNumber.length < 7) {
      return f({id: 'CLAIM.REGISTER.PHONE_TOO_SHORT'})
    }

    const totalDigits = country.dialCode.replace('+', '').length + localNumber.length
    if (totalDigits > 15) {
      return f({id: 'CLAIM.REGISTER.PHONE_TOO_LONG'})
    }

    return null
  }

  const validateFields = (
    appBasicData: typeof data.appBasic,
    localPhone = localPhoneNumber,
    country: Country = selectedCountry
  ) => {
    const planValid = !!appBasicData.plan
    const error = validatePhone(localPhone, country)
    const phoneNumberValid = !error
    setPhoneError(error)

    const benefitsValid = !!selectedBenefit
    const treatmentTypeValid = !!appBasicData.treatmentType

    // Make indate validation conditional on treatment type
    // Only required for "Rawat inap", always valid for "Rawat jalan"
    const indateValid =
      appBasicData.treatmentType === 'Rawat jalan'
        ? true
        : !!(appBasicData.indate && appBasicData.indate.trim() !== '')

    setIsPlanValid(planValid)
    setIsBenefitsValid(benefitsValid)
    setIsTreatmentTypeValid(treatmentTypeValid)
    setIsIndateValid(indateValid)

    // Include indate validation in overall validity check when needed
    const allFieldsValid =
      planValid && phoneNumberValid && benefitsValid && treatmentTypeValid && indateValid

    if (allFieldsValid) {
      handleStep2ValidationSuccess && handleStep2ValidationSuccess()
      console.log('Complete')
    } else {
      handleStep2ValidationError && handleStep2ValidationError()
      console.log('Incomplete')
    }
  }

  const handleInputChange = (field: string, value: string) => {
    // Regular field handling for other fields
    const updatedData = {
      appBasic: {
        ...data.appBasic,
        [field]: value,
      },
    }

    // Special handling for treatment type
    if (field === 'treatmentType') {
      // If switching to "Rawat inap", reset the indate value
      if (value === 'Rawat inap') {
        updatedData.appBasic.indate = '' // Reset indate when Rawat inap is selected
      }
      // If switching to "Rawat jalan", set indate to current date
      else if (value === 'Rawat jalan') {
        const currentDate = new Date().toISOString().split('T')[0] // Format: YYYY-MM-DD
        updatedData.appBasic.indate = currentDate
      }
    }

    updateData(updatedData)
    validateFields(updatedData.appBasic)
  }

  const handlePhoneChange = (localNumber: string, country = selectedCountry) => {
    setLocalPhoneNumber(localNumber)
    setSelectedCountry(country)

    const assembledValue = country.dialCode + localNumber

    const updatedData = {
      appBasic: {
        ...data.appBasic,
        phoneNumber: assembledValue,
      },
    }

    updateData(updatedData)
    validateFields(updatedData.appBasic, localNumber, country)
  }

  return (
    <div className='pb-5' data-kt-stepper-element='content'>
      <div className='w-100'>
        {/* Indate Input */}
        <CustomCalendar
          className='mb-10'
          value={data.appBasic.indate || ''}
          onChange={(date) => handleInputChange('indate', date)}
          label={
            <span>
              {/* Tanggal Masuk */}
              {f({id: 'CLAIM.REGISTER.INDATE'})}
              {/* Show the required asterisk only for Rawat inap */}
              {data.appBasic.treatmentType === 'Rawat inap' && (
                <span style={{color: '#059BB6'}}> *</span>
              )}
            </span>
          }
          placeholder={f({id: 'CLAIM.REGISTER.CHOOSE_INDATE'})}
          // required={data.appBasic.treatmentType === 'Rawat inap'}
          disabled={data.appBasic.treatmentType === 'Rawat jalan'}
          error={
            !isIndateValid && data.appBasic.treatmentType === 'Rawat inap'
              ? 'Tanggal Indate wajib diisi untuk Rawat inap.'
              : undefined
          }
          // minDate={new Date()}
        />

        {/* Begin::Form Group for Manfaat */}
        <div className='fv-row mb-10'>
          <label
            className='d-flex align-items-center fs-5 fw-semibold mb-2'
            style={{color: '#6E7191'}}
          >
            <span className='ms-2'>{f({id: 'CLAIM.REGISTER.BENEFIT'})}</span>
            <span style={{color: '#059BB6'}}> * </span>
          </label>
          <select
            className={`form-select form-select-lg ${!isBenefitsValid ? 'is-invalid' : ''}`}
            value={selectedBenefit ? selectedBenefit.id : ''}
            onChange={handleBenefitChange}
            onFocus={handleDropdownOpen}
          >
            <option value='' disabled>
              {f({id: 'CLAIM.REGISTER.CHOOSE_BENEFIT'})}
            </option>
            {benefits.map((benefit) => (
              <option key={benefit.id} value={benefit.id}>
                {benefit.benefitName}
              </option>
            ))}
          </select>
          {errorMessageBenefit && (
            <div
              className='mt-4'
              style={{
                border: '1px solid #F15E60',
                borderRadius: '6px',
                padding: '10px',
                backgroundColor: '#FFFFFF',
              }}
            >
              <div
                className='d-flex align-items-center'
                style={{color: '#F15E60', fontWeight: 'bold'}}
              >
                <KTIcon iconName='cross-circle' className='me-2 custom-red large-icon' />
                {f({id: 'CLAIM.REGISTER.PATIENT'})}
              </div>
              <hr style={{border: '1px solid #F15E60', margin: '10px 0'}} />
              <div style={{color: '#333'}}>
                <span style={{color: '#b2b7bf'}}>{data.appBasic.cardNumber}</span>{' '}
                {errorMessageBenefit}
              </div>
            </div>
          )}
        </div>
        {/* End::Form Group */}

        {/* Begin::Form Group for Treatment Type */}
        <div className='fv-row mb-10'>
          <label
            className='d-flex align-items-center fs-5 fw-semibold mb-2'
            style={{color: '#6E7191'}}
          >
            <span className='ms-2'>{f({id: 'CLAIM.REGISTER.TREATMENT_TYPE'})}</span>
            <span style={{color: '#059BB6'}}> *</span>
          </label>
          <div className='d-flex'>
            <div className='form-check form-check-custom me-5'>
              <input
                className='form-check-input'
                type='radio'
                name='treatmentType'
                id='treatmentTypeInpatient'
                checked={data.appBasic.treatmentType === 'Rawat inap'}
                onChange={() => handleInputChange('treatmentType', 'Rawat inap')}
                disabled={
                  !!selectedBenefit &&
                  ![
                    'INPATIENT',
                    'INPATIENT-OUTPATIENT-KHUSUS',
                    'MATERNITY',
                    'LIMIT BOOSTER TAHUNAN',
                    'LIFETIME LIMIT BENEFIT',
                    // 'OUTPATIENT',
                  ].includes(data?.appBasic?.benefit)
                  // ].includes(data?.appBasic?.memberData?.insurerCode)
                }
              />
              <label
                className='form-check-label'
                htmlFor='treatmentTypeInpatient'
                style={{color: '#6E7191'}}
              >
                {f({id: 'CLAIM.REGISTER.INPATIENT'})}
              </label>
            </div>
            <div className='form-check form-check-custom'>
              <input
                className='form-check-input'
                type='radio'
                name='treatmentType'
                id='treatmentTypeOutpatient'
                checked={data.appBasic.treatmentType === 'Rawat jalan'}
                onChange={() => handleInputChange('treatmentType', 'Rawat jalan')}
                disabled={[
                  '22110009',
                  'PDL',
                  'ALLISYA',
                  'AFI',
                  'ALLIANZ',
                  'CHUI',
                  'JD',
                  'AAL',
                  'FWDII',
                  'EQI',
                  'AXM',
                ].includes(data?.appBasic?.memberData?.insurerCode)}
              />
              <label
                className='form-check-label'
                htmlFor='treatmentTypeOutpatient'
                style={{color: '#6E7191'}}
              >
                {f({id: 'CLAIM.REGISTER.OUTPATIENT'})}
              </label>
            </div>
          </div>
          {!isTreatmentTypeValid && (
            <div className='invalid-feedback d-block'>
              {f({id: 'CLAIM.REGISTER.TREATMENT_TYPE_VALIDATION'})}.
            </div>
          )}
        </div>
        {/* End::Form Group */}

        {/* Begin::Form Group for Plan Peserta */}
        <div className='fv-row mb-10'>
          <label
            className='d-flex align-items-center fs-5 fw-semibold mb-2'
            style={{color: '#6E7191'}}
          >
            <span className='ms-2'>{f({id: 'CLAIM.REGISTER.PLAN_PARTICIPANT'})}</span>
            <span style={{color: '#059BB6'}}> *</span>
          </label>
          <input
            type='text'
            className={`form-control form-control-lg  ${!isPlanValid ? 'is-invalid' : ''}`}
            placeholder='IP 100'
            value={data.appBasic.plan || (selectedBenefit ? selectedBenefit.planName : '')}
            onChange={(e) => handleInputChange('plan', e.target.value)}
            readOnly={true}
          />
          {/* Error Message Display */}
          {errorMessageProduct && (
            <div
              className='mt-4'
              style={{
                border: '1px solid #F15E60',
                borderRadius: '6px',
                padding: '10px',
                backgroundColor: '#FFFFFF',
              }}
            >
              <div
                className='d-flex align-items-center'
                style={{color: '#F15E60', fontWeight: 'bold'}}
              >
                <KTIcon iconName='cross-circle' className='me-2 custom-red large-icon' />
                {f({id: 'CLAIM.REGISTER.PATIENT'})}
              </div>
              <hr style={{border: '1px solid #F15E60', margin: '10px 0'}} />
              <div style={{color: '#333'}}>
                <span style={{color: '#b2b7bf'}}>{data.appBasic.cardNumber}</span>{' '}
                {errorMessageProduct}
              </div>
            </div>
          )}
        </div>
        {/* End::Form Group */}

        {/* Begin::Form Group for No. Telp. Pasien/Pendamping */}
        <div className='fv-row mb-10'>
          <label
            className='d-flex align-items-center fs-5 fw-semibold mb-2'
            style={{color: '#6E7191'}}
          >
            <span className='ms-2'>{f({id: 'CLAIM.REGISTER.PATIENT_PHONE_NUMBER'})} </span>
            <span style={{color: '#059BB6'}}> *</span>
          </label>
          <div className='d-flex gap-3'>
            <div style={{width: '30%'}}>
              <div className='dropdown w-100'>
                <button
                  className='btn btn-lg btn-light w-100 d-flex align-items-center justify-content-between border'
                  type='button'
                  data-bs-toggle='dropdown'
                  aria-expanded='false'
                  style={{
                    backgroundColor: '#F9F9F9',
                    borderColor: '#E1E3EA',
                    padding: '0.75rem 1rem',
                  }}
                >
                  <div className='d-flex align-items-center overflow-hidden'>
                    {selectedCountry.flagUrl ? (
                      <img
                        src={selectedCountry.flagUrl}
                        alt={selectedCountry.code}
                        style={{width: '24px', height: '16px', marginRight: '8px', objectFit: 'cover'}}
                      />
                    ) : (
                      <span style={{marginRight: '8px'}}>{selectedCountry.emoji}</span>
                    )}
                    <span className='fw-bold'>{selectedCountry.dialCode}</span>
                  </div>
                  <i className='bi bi-chevron-down fs-8 ms-2'></i>
                </button>
                <ul
                  className='dropdown-menu shadow-lg mt-1 p-0 border'
                  style={{
                    minWidth: '350px',
                    zIndex: '9999 !important' as any,
                    backgroundColor: '#ffffff',
                    overscrollBehavior: 'contain',
                    borderRadius: '8px',
                    overflow: 'hidden',
                  }}
                >
                  <li
                    className='p-3 sticky-top bg-white border-bottom'
                    onClick={(e) => e.stopPropagation()}
                    style={{zIndex: 10}}
                  >
                    <div className='position-relative'>
                      <KTIcon
                        iconName='magnifier'
                        className='position-absolute top-50 translate-middle-y ms-3'
                      />
                      <input
                        type='text'
                        className='form-control form-control-solid ps-10'
                        placeholder='Search country or code...'
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        autoFocus
                      />
                    </div>
                  </li>
                  <div
                    style={{
                      maxHeight: '350px',
                      overflowY: 'auto',
                      overflowX: 'hidden',
                    }}
                    className='custom-scrollbar'
                  >
                    {filteredCountries.length > 0 ? (
                      filteredCountries.map((c) => (
                        <li key={c.code}>
                          <button
                            className='dropdown-item d-flex align-items-center py-3'
                            type='button'
                            onClick={() => handlePhoneChange(localPhoneNumber, c)}
                            style={{whiteSpace: 'normal'}}
                          > 
                            {c.flagUrl ? (
                              <img
                                src={c.flagUrl}
                                alt={c.code}
                                style={{
                                  width: '24px',
                                  height: '16px',
                                  marginRight: '12px',
                                  objectFit: 'cover',
                                  borderRadius: '2px',
                                }}
                              />
                            ) : (
                              <span style={{marginRight: '12px'}}>{c.emoji}</span>
                            )}
                            <span className='flex-grow-1 fs-6 text-dark'>{c.name}</span>
                            <span className='text-muted fs-7 ms-2'>{c.dialCode}</span>
                          </button>
                        </li>
                      ))
                    ) : (
                      <li className='p-4 text-center text-muted fs-7'>No countries found</li>
                    )}
                  </div>
                </ul>
              </div>
            </div>
            <div className='flex-grow-1'>
              <input
                type='text'
                className={`form-control form-control-lg ${phoneError ? 'is-invalid' : ''}`}
                placeholder='8123...'
                value={localPhoneNumber}
                onChange={(e) => handlePhoneChange(e.target.value)}
                onBlur={() => validateFields(data.appBasic, localPhoneNumber, selectedCountry)}
              />
            </div>
          </div>
          {phoneError && <div className='invalid-feedback d-block mt-2'>{phoneError}</div>}
        </div>
        {/* End::Form Group */}

        {/* Begin::Form Group for No. Rekam Medis */}
        <div className='fv-row mb-10'>
          <label
            className='d-flex align-items-center fs-5 fw-semibold mb-2'
            style={{color: '#6E7191'}}
          >
            <span className='ms-2'>{f({id: 'CLAIM.REGISTER.MEDICAL_RECORD_NUMBER'})} </span>
            {/* <span style={{ color: '#059BB6' }}> *</span> */}
          </label>
          <input
            type='text'
            // className={`form-control form-control-lg form-control-solid ${!isMedicalRecordValid ? 'is-invalid' : ''}`}
            className={`form-control form-control-lg `}
            value={data.appBasic.medicalRecordNumber}
            onChange={(e) => {
              const value = e.target.value
              handleInputChange('medicalRecordNumber', value)
            }}
          />
          {/* {!isMedicalRecordValid && <div className="invalid-feedback">Medical record number is required.</div>} */}
        </div>
        {/* End::Form Group */}

        {/* Begin::Form Group for Asal Rujukan */}
        <div className='fv-row mb-10'>
          <label
            className='d-flex align-items-center fs-5 fw-semibold mb-2'
            style={{color: '#6E7191'}}
          >
            <span className='ms-2'>{f({id: 'CLAIM.REGISTER.REFERRAL_SOURCE'})} </span>
            {/* <span style={{ color: '#059BB6' }}> *</span> */}
          </label>
          <input
            type='text'
            // className={`form-control form-control-lg form-control-solid ${!isReferralSourceValid ? 'is-invalid' : ''}`}
            className={`form-control form-control-lg `}
            placeholder=''
            value={data.appBasic.referralSource}
            onChange={(e) => {
              const value = e.target.value
              handleInputChange('referralSource', value)
            }}
          />
          {/* {!isReferralSourceValid && <div className="invalid-feedback">Asal Rujukan wajib diisi.</div>} */}
        </div>
        {/* End::Form Group */}

        {/* Begin::Form Group for Catatan */}
        <div className='fv-row mb-10'>
          <label
            className='d-flex align-items-center fs-5 fw-semibold mb-2'
            style={{color: '#6E7191'}}
          >
            <span className='ms-2'>{f({id: 'CLAIM.REGISTER.NOTES'})} </span>
            {/* <span style={{ color: '#059BB6' }}> *</span> */}
          </label>
          <textarea
            // className={`form-control form-control-lg form-control-solid ${!isNotesValid ? 'is-invalid' : ''}`}
            className={`form-control form-control-lg`}
            rows={3}
            value={data.appBasic.notes}
            onChange={(e) => {
              const value = e.target.value
              handleInputChange('notes', value)
            }}
          />
          {/* {!isNotesValid && <div className="invalid-feedback">Catatan wajib diisi.</div>} */}
        </div>
        {/* End::Form Group */}
      </div>
    </div>
  )
}

export {Step2}
